import React, { createContext, useContext, useState } from 'react';

const AuthContext = createContext(null);

// "View as MFD" sessions (opened by an admin in a new tab) live under these
// distinct sessionStorage keys — scoped to this tab only — so they never
// touch the 'token'/'user' localStorage keys the admin's own tab reads from.
const IMPERSONATION_TOKEN_KEY = 'impersonation_token';
const IMPERSONATION_USER_KEY  = 'impersonation_user';

// Runs once when this tab's JS loads. If the URL carries an #impersonate=
// payload (only ever set by the admin's "view as MFD" link), decode it into
// this tab's own sessionStorage and strip it from the address bar.
(function consumeImpersonationHash() {
  const match = (window.location.hash || '').match(/(?:^#|&)impersonate=([^&]+)/);
  if (!match) return;
  try {
    const json = decodeURIComponent(escape(atob(decodeURIComponent(match[1]))));
    const payload = JSON.parse(json);
    if (payload.access_token && payload.user) {
      sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, payload.access_token);
      sessionStorage.setItem(IMPERSONATION_USER_KEY, JSON.stringify(payload.user));
    }
  } catch {
    // malformed payload — ignore, fall through to normal auth
  }
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
})();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const impersonatedUser = sessionStorage.getItem(IMPERSONATION_USER_KEY);
    if (impersonatedUser) {
      try { return JSON.parse(impersonatedUser); } catch { return null; }
    }
    const saved = localStorage.getItem('user');
    if (saved) {
      try { return JSON.parse(saved); } catch { return null; }
    }
    return null;
  });
  const [token, setToken] = useState(sessionStorage.getItem(IMPERSONATION_TOKEN_KEY) || localStorage.getItem('token'));

  const login = (tokenVal, userData) => {
    localStorage.setItem('token', tokenVal);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(tokenVal);
    setUser(userData);
  };

  const logout = () => {
    // An impersonation tab's session lives only in sessionStorage. Clearing
    // localStorage here would also log out the admin's own tab, since
    // localStorage is shared across all tabs on the same origin.
    if (sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)) {
      sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
      sessionStorage.removeItem(IMPERSONATION_USER_KEY);
      setToken(null);
      setUser(null);
      return;
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const updateUser = (patch) => {
    setUser(prev => {
      const next = { ...prev, ...patch };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
